import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import Snackbar from "@material-ui/core/Snackbar";
import InstrumentApi from "apis/instrumentApi";
import useSound from "use-sound";
import AlarmIcon from "assets/icons/notifications_active.svg";
import { setInstrumentOperation } from "ducks/instrumentDetails/actions";
import { setSidebarType } from "ducks/account/actions";
import ClearIcon from "assets/icons/clearLight.svg";
import { makeStyles } from "@material-ui/core/styles";
import { BLACK_APLHA, BLACK, SPANISH_RED } from "constants/colors";
import { SIDEBAR_TYPES, snackbarPositions } from "constants/globals";
import history from "utils/browserHistory";
import { isModulePathChange } from "utils/modulePath";
import { createTtlDedupe, NOTIFICATION_DEDUPE_MS } from "utils/ttlDedupe";

// $FlowFixMe
const soundAlarm = require("assets/sounds/sound-alarm.mp3");

// ENT-5801: la notificación se cierra sola a los 6s (sin pausa por hover/blur)
export const AUTO_HIDE_DURATION_MS = 6000;

// Guard a nivel módulo: el sonido suena una sola vez por evento aunque el
// componente se remonte (cambio de sección) con el mismo mensaje.
const shouldPlaySound = createTtlDedupe(NOTIFICATION_DEDUPE_MS);

const maxContent = "max-content";
const useStyles = makeStyles(theme => ({
  snackbar: {
    display: "flex",
    alignItems: "center",
    color: "white",
    marginTop: 30,
    paddingTop: 11,
    paddingBottom: 11,
    marginBottom: 40,
    paddingLeft: 18,
    paddingRight: 18,
    borderRadius: 4,
    backgroundColor: theme.palette.GRAY_EVEN_WHITE_SMOKE,
    width: 400
  },
  copyInfo: {
    paddingLeft: "65px !important",
    marginBottom: 95,
    width: 340,
    boxShadow: `0 8px 16px 0 ${BLACK_APLHA}`
  },
  message: {
    fontSize: 14,
    fontFamily: "Roboto",
    height: maxContent,
    width: maxContent,
    fontWeight: 500,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.43,
    letterSpacing: "normal",
    color: theme.palette.WHITE_BLACK
  },
  icon: {
    height: 20,
    width: 20,
    marginRight: 15
  },
  iconClear: {
    height: 20,
    width: 20,
    cursor: "pointer",
    float: "right"
  },
  separator: {
    width: "24px",
    height: "0px",
    right: "82px",
    border: "1px solid #CEDAEB",
    transform: "rotate(90deg)"
  },
  operateButton: {
    cursor: "pointer",
    height: 27,
    width: 68,
    position: "absolute",
    right: 35,
    borderRadius: 8,
    backgroundColor: "#E2E5E7",
    padding: "4px 8px 4px 8px"
  },
  operate: {
    fontSize: 16,
    fontFamily: "Lato",
    maxWidth: "calc(100vw - 200px)",
    fontWeight: 700,
    color: BLACK,
    letterSpacing: 0.25
  }
}));

type Props = {
  message: string,
  handleClose: Function,
  instrumentId: string,
  orderId: ?number,
  handleClick: Function,
  isFounds?: Boolean,
  // Id estable del evento (alarmId / folioOrden); si falta se usa el mensaje
  notificationId?: string | number
};

const MaterialSnackbar = (props: Props) => {
  const [play, { sound }] = useSound(soundAlarm, { volume: 0.5, html5: true });

  const dispatch = useDispatch();

  const {
    message,
    handleClose,
    instrumentId,
    orderId,
    handleClick,
    isFounds,
    notificationId
  } = props;
  const classes = useStyles();
  // Los hosts pasan closures nuevas en cada render; el ref evita reiniciar el timer
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;
  const messageRef = useRef(message);
  messageRef.current = message;
  const pathRef = useRef(
    history.location && history.location.pathname
      ? history.location.pathname
      : ""
  );

  const eventKey =
    notificationId !== undefined && notificationId !== null
      ? notificationId
      : message;
  // Notificación vigente: la respuesta tardía de una alarma anterior no cierra la actual
  const eventKeyRef = useRef(eventKey);
  eventKeyRef.current = eventKey;

  /**
   * Abre el panel de compra del instrumento y cierra el toast solo si la
   * notificación sigue siendo la misma al resolver la petición.
   */
  const openBuyOperation = () => {
    const startedKey = eventKey;
    InstrumentApi.getInstrumentData(props.instrumentId)
      // eslint-disable-next-line
      .then(({ data }) => {
        if (data) {
          const emisora = data[0].emisora ? data[0].emisora.trim() : "";
          const serie = data[0].serie ? data[0].serie.trim() : "";
          dispatch(
            setInstrumentOperation(
              {
                symbol: data[0].descripcion,
                issuer: emisora,
                series: serie,
                uniqueKey: props.instrumentId,
                tradingStatus: data[0].tradingStatus
                  ? data[0].tradingStatus
                  : true,
                name: data[0].descripcionInstrumento
              },
              "1"
            )
          );
          dispatch(setSidebarType(SIDEBAR_TYPES.OPERATE));
          if (eventKeyRef.current === startedKey) {
            handleClose();
          }
        }
      })
      .catch(errors => {
        Promise.resolve(errors);
      });
  };

  const styleOperation = () => {
    if (instrumentId) return { maxWidth: "235px" };
    return {};
  };

  useEffect(() => {
    if (!message || !sound) {
      return;
    }
    if (!shouldPlaySound(eventKey)) {
      return;
    }
    try {
      play();
    } catch (_err) {
      /* Safari / autoplay: evitar propagación síncrona; sonido es opcional */
    }
  }, [message, eventKey, play, sound]);

  // Cierra al pasar entre Mis Mercados, Análisis y Portafolio.
  // El shell no se desmonta, así que el path es la señal del cambio.
  useEffect(() => {
    const unlisten = history.listen(location => {
      const nextPath = location.pathname;
      const previousPath = pathRef.current;
      pathRef.current = nextPath;
      if (messageRef.current && isModulePathChange(previousPath, nextPath)) {
        handleCloseRef.current({}, "route");
      }
    });
    return () => {
      if (typeof unlisten === "function") {
        unlisten();
      }
    };
    // El path vive en refs; la suscripción es una sola por montaje del shell
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autocierre: se reinicia solo cuando cambia la notificación mostrada
  useEffect(() => {
    if (!message) {
      return undefined;
    }
    const timerId = setTimeout(() => {
      handleCloseRef.current({}, "timeout");
    }, AUTO_HIDE_DURATION_MS);
    return () => clearTimeout(timerId);
  }, [message, eventKey]);

  // Un click sobre el propio toast lo cierra; las acciones detienen la
  // propagación para no cerrar dos veces (ya limpian el estado por su cuenta)
  const stopPropagation = (event: Object) => event.stopPropagation();

  return (
    <Snackbar
      anchorOrigin={{
        vertical: snackbarPositions.TOP,
        horizontal: "center"
      }}
      open={!!message}
    >
      <div
        className={`${classes.snackbar}`}
        role="presentation"
        onClick={() => handleClose({}, "click")}
      >
        <img src={AlarmIcon} className={classes.icon} alt="AlarmIcon" />
        <span className={`${classes.message}`} style={styleOperation()}>
          {message}{" "}
          {orderId && !isFounds && (
            <>
              <div
                className={`${classes.message}`}
                style={{
                  display: "inline-block",
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontWeight: "bold",
                  color: SPANISH_RED
                }}
                role={"button"}
                onClick={event => {
                  stopPropagation(event);
                  handleClick(orderId);
                }}
                onKeyDown={() => handleClick(orderId)}
                tabIndex={0}
              >
                <span>Consultar órdenes</span>
              </div>
              <span>&#34;</span>
            </>
          )}
        </span>

        {instrumentId && (
          <div
            className={`${classes.operateButton}`}
            role={"button"}
            onClick={event => {
              stopPropagation(event);
              openBuyOperation();
            }}
            onKeyDown={() => openBuyOperation()}
            tabIndex={0}
          >
            <div className={`${classes.operate}`}>Operar</div>
          </div>
        )}
        <div
          role={"button"}
          onKeyDown={handleClose}
          tabIndex={0}
          style={{
            position: "absolute",
            right: 12
          }}
        >
          <img src={ClearIcon} className={classes.iconClear} alt="ClearIcon" />
        </div>
      </div>
    </Snackbar>
  );
};

export default MaterialSnackbar;
